import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { color, radius } from "../../styles/theme";

/** Windowed page-number list with "…" gaps — e.g. [1, "…", 4, 5, 6, "…", 42]. */
const getPageWindow = (current, totalPages, delta = 1) => {
  const range = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }
  const withDots = [];
  let last = null;
  range.forEach((i) => {
    if (last != null) {
      if (i - last === 2) withDots.push(last + 1);
      else if (i - last !== 1) withDots.push("…");
    }
    withDots.push(i);
    last = i;
  });
  return withDots;
};

/**
 * Page-number pagination — replaces the "Load More" append pattern on list
 * pages (Notifications, Wallet, Cashout History, Event History). Purely a
 * display/navigation control: the caller still owns fetching — `onPageChange`
 * is expected to load and REPLACE that page's data, not append to it.
 */
const Pagination = ({ page, pageSize, totalCount, onPageChange, disabled = false }) => {
  const { t } = useTranslation("common");
  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / (pageSize || 1)));
  if (totalPages <= 1) return null;

  const pages = getPageWindow(page, totalPages);
  const rangeStart = (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount || 0);

  return (
    <div style={s.wrap}>
      <span style={s.rangeText}>
        {t("pagination.range", { start: rangeStart, end: rangeEnd, total: totalCount || 0 })}
      </span>
      <div style={s.controls}>
        <button
          type="button"
          style={s.navBtn}
          onClick={() => onPageChange(page - 1)}
          disabled={disabled || page <= 1}
          aria-label={t("pagination.previous")}
        >
          <ChevronLeft size={15} strokeWidth={2.25} />
        </button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`dots-${i}`} style={s.dots}>…</span>
          ) : (
            <button
              key={p}
              type="button"
              style={{ ...s.pageBtn, ...(p === page ? s.pageBtnActive : {}) }}
              onClick={() => p !== page && onPageChange(p)}
              disabled={disabled}
              aria-current={p === page ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          style={s.navBtn}
          onClick={() => onPageChange(page + 1)}
          disabled={disabled || page >= totalPages}
          aria-label={t("pagination.next")}
        >
          <ChevronRight size={15} strokeWidth={2.25} />
        </button>
      </div>
    </div>
  );
};

const s = {
  wrap: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginTop: 20 },
  rangeText: { fontSize: 12.5, color: color.textMuted },
  controls: { display: "flex", alignItems: "center", gap: 5 },
  navBtn: {
    width: 30, height: 30, borderRadius: radius.sm, border: `1px solid ${color.border}`,
    background: color.surface, color: color.gray700, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
  },
  pageBtn: {
    minWidth: 30, height: 30, padding: "0 8px", borderRadius: radius.sm, border: `1px solid ${color.border}`,
    background: color.surface, color: color.gray700, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
  },
  pageBtnActive: { border: `1px solid ${color.primary500}`, background: color.primary500, color: "#fff" },
  dots: { padding: "0 4px", color: color.textMuted, fontSize: 12.5 },
};

export default Pagination;
